import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslocoService } from '@jsverse/transloco';

import { SearchComponent } from '@connect-services/client/components/search/search.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export const FIRST_COMPLEX_MODAL_STEP = 1;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule, SearchComponent],
    selector: 'app-complex-modal-template',
    styleUrl: './complex-modal-template.component.scss',
    templateUrl: './complex-modal-template.component.html',
})
export class ComplexModalTemplate {
    public readonly canCancel = input<boolean>(false);
    public readonly canClose = input<boolean>(true);
    public readonly canConfirm = input<boolean>(false);
    public readonly confirmIcon = input<IconProp | null>(null);
    public readonly confirmLabel = input<string>('confirm');
    public readonly confirmTooltip = input<string | undefined>(undefined);
    public readonly currentStep = model(FIRST_COMPLEX_MODAL_STEP);
    public readonly customLabel = input<string | null>(null);
    public readonly disableClose = input<boolean>(true);
    public readonly isConfirmationMode = input<boolean>(true);
    public readonly isSearchbarActive = input<boolean>(false);
    public readonly messageInformation = input<string>();
    public onCancelClick = output<void>();
    public onClickEvent = output<ModalResultEnum>();
    public readonly searchInput = model<string>('');
    public readonly searchLabelKey = input<string>('searchData');
    public readonly showBackButtonOnFirstStep = input<boolean>(false);
    public readonly stepNumber = input<number>(1);
    public readonly title = input<string>('');
    public readonly titleSize = input<string | null>(null);

    protected readonly buttonLabel = computed<string>(() => {
        const label = this.customLabel();
        if (label) {
            return label;
        }

        return this.translocoService.translate(this.isLastStep() ? this.confirmLabel() : 'nextStep');
    });
    protected readonly ButtonType = ButtonType;
    protected readonly isFirstStep = computed<boolean>(() => this.currentStep() === 1);
    protected readonly isLastStep = computed<boolean>(() => this.currentStep() === this.stepNumber());
    protected readonly ModalResultEnum = ModalResultEnum;
    protected readonly searchControl = new FormControl('', { nonNullable: true });
    protected readonly showBackButton = computed<boolean>(
        () => !this.isFirstStep() || this.showBackButtonOnFirstStep(),
    );

    private dialogRef = inject(MatDialogRef);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.dialogRef.disableClose = this.disableClose();
    }

    public nextStep(): void {
        this.currentStep.set(this.currentStep() + 1);
    }

    public onBackClick(): void {
        if (this.isFirstStep()) {
            this.onClickEvent.emit(ModalResultEnum.CANCEL);

            return;
        }

        this.previousStep();
    }

    public previousStep(): void {
        this.currentStep.set(this.currentStep() - 1);
    }

    protected updateSearchTerm(values: string): void {
        this.searchInput.set(values ?? '');
    }
}
